import { useLingui } from '@lingui/react/macro';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { I18nWording, useDatabaseTranslation } from '@/services/i18n';
import { useFetch } from '@/utils/fetch';

import Phone from './partials/phone';
import { AddressRaw } from './address';

export const useGetWorkshopRegion = () => {
  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();

  const otherRegion = t({ id: 'workshop.region.other', message: 'Other' });

  return (workshop: {
    entityOrganizations: { workshopConfig: null | { region: I18nWording } }[];
  }) => {
    const region = workshop.entityOrganizations[0]?.workshopConfig?.region;

    return region ? _db(region) : otherRegion;
  };
};

export const useWorkshop = (
  id?: string | null,
  options?: {
    enabled?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /workshops/:id']>();

  return useQuery({
    queryKey: ['workshops', id],
    queryFn: () => fetch(`/workshops/${id!}`),
    enabled: !!id && options?.enabled !== false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
};

export type WorkshopFromGetMany = Endpoints['GET /workshops']['response']['workshops'][number];
export const useWorkshops = (
  params: Endpoints['GET /workshops']['query'] = {},
  options?: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  }
) => {
  const fetch = useFetch<Endpoints['GET /workshops']>();

  return useQuery({
    queryKey: ['workshops', params],
    queryFn: () => fetch('/workshops', params),
    enabled: options?.enabled,
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined,
    refetchOnWindowFocus: !!params.articleId,
    refetchOnReconnect: !!params.articleId,
    refetchOnMount: !!params.articleId,
  });
};

export const useUpdateWorkshop = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /workshops/:id']>();

  return useMutation({
    mutationFn: ({
      workshopId,
      ...body
    }: {
      workshopId: string;
      name: string;
      groups: { id: string }[];
      address: AddressRaw;
      phone: Phone;
      qualityControllers?: string[];
    }) => fetch(`/workshops/${workshopId}`, undefined, { method: 'PATCH', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workshops'] });
    },
  });
};
